import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/AuthContext.jsx';
import { Field, useToast, Progress } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import Illustration from '../components/Illustration.jsx';
import AuthShell, { AuthHead } from '../components/AuthShell.jsx';
import { firstName } from '../lib/format.js';

/** Cheap, honest strength read — length and variety, no false precision. */
function strengthOf(pw) {
  if (!pw) return { score: 0, label: '', tone: 'var(--text-3)' };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const bands = [
    { label: 'Too short', tone: 'var(--danger)' },
    { label: 'Weak', tone: 'var(--danger)' },
    { label: 'Fair', tone: 'var(--warning)' },
    { label: 'Good', tone: 'var(--warning)' },
    { label: 'Strong', tone: 'var(--success)' },
    { label: 'Very strong', tone: 'var(--success)' },
  ];
  return { score, ...bands[score] };
}

/**
 * Step 2 of the reset flow: choose the new password.
 *
 * The link is validated on mount so an expired or spent token is reported
 * before the user types anything, rather than after they have filled in and
 * confirmed a password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { resetPassword } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [check, setCheck] = useState({ state: 'checking' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return setCheck({ state: 'invalid', error: 'This link is missing its reset token.' });
    let alive = true;
    api
      .get(`/api/auth/reset/${encodeURIComponent(token)}`)
      .then((r) => alive && setCheck({ state: 'valid', ...r }))
      .catch((err) => alive && setCheck({ state: 'invalid', error: err.message }))
      .finally(() => {});
    return () => {
      alive = false;
    };
  }, [token]);

  const strength = strengthOf(password);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 6 && confirm === password && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await resetPassword(token, password);
      toast.success(`Password updated. Welcome back, ${firstName(user.name)}.`);
      navigate(user.role === 'nurse' ? '/nurse' : user.role === 'admin' ? '/admin' : '/', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  /* ------------------------------------------------------ Link checking */
  if (check.state === 'checking') {
    return (
      <AuthShell>
        <AuthHead title="Checking your link…" />
        <div className="stack-sm">
          <div className="skeleton skeleton-text" style={{ height: 44, borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton skeleton-text" style={{ height: 44, borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton" style={{ height: 50, borderRadius: 'var(--radius-pill)' }} />
        </div>
      </AuthShell>
    );
  }

  if (check.state === 'invalid') {
    return (
      <AuthShell back={{ to: '/login', label: 'Back to sign in' }}>
        <div className="center">
          <Illustration name="not-found" width={180} />
          <h1 className="t-h1 mt-2">This link no longer works</h1>
          <p className="muted mt-1">{check.error}</p>
          <p className="tiny dim mt-2">
            Reset links expire after an hour and can only be used once. Request a fresh one and it will arrive
            in a moment.
          </p>
          <div className="stack-sm mt-3">
            <Link to="/forgot-password" className="btn btn-primary btn-block btn-lg">
              Send a new link
            </Link>
            <Link to="/login" className="btn btn-ghost btn-block">
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  /* -------------------------------------------------------- The form */
  return (
    <AuthShell back={{ to: '/login', label: 'Back to sign in' }}>
      <AuthHead
        title="Choose a new password"
        subtitle={check.email ? `for ${check.email}` : undefined}
      />

      {error && (
        <div className="banner banner-danger">
          <Icon name="warning" />
          <span className="grow">{error}</span>
        </div>
      )}

      <form onSubmit={submit}>
        <Field label="New password" id="rp-password" hint="At least 6 characters." required>
          <div className="input-icon">
            <input
              id="rp-password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              autoFocus
              required
              style={{ paddingLeft: 14, paddingRight: 46 }}
            />
            <button
              type="button"
              className="input-clear"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              <Icon name="eye" />
            </button>
          </div>
        </Field>

        {password && (
          <div className="mb-2" style={{ marginTop: -8 }}>
            <Progress value={strength.score} max={5} label="Password strength" />
            <div className="row-between mt-1">
              <span className="tiny dim">Password strength</span>
              <span className="tiny bold" style={{ color: strength.tone }}>
                {strength.label}
              </span>
            </div>
          </div>
        )}

        <Field
          label="Confirm new password"
          id="rp-confirm"
          required
          error={mismatch ? 'Both passwords must match.' : undefined}
        >
          <input
            id="rp-confirm"
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            aria-invalid={mismatch || undefined}
            required
          />
        </Field>

        <button className="btn btn-primary btn-lg btn-block" disabled={!canSubmit}>
          {busy ? <span className="spinner" /> : 'Set new password'}
        </button>
      </form>

      <div className="banner banner-neutral mt-3" style={{ marginBottom: 0 }}>
        <Icon name="lock" />
        <span className="grow tiny">
          Setting a new password signs you in and invalidates every other reset link for this account.
        </span>
      </div>
    </AuthShell>
  );
}
